import React, {FC, useCallback, useEffect, useState} from 'react'
import {useHistory} from 'react-router-dom'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {createHrProcess, deleteHrProcess, loadHrProcesses, updateHrProcess} from './Api'
import {IHrProcessModel} from './HrProcessModel'
import {HrProcessTable} from './HrProcessTable'
import {HrProcessFormModal} from './HrProcessFormModal'
import {getMockHrProcessPage, USE_HR_PROCESS_MOCK} from './HrProcessMockData'
import {HR_PROCESS_FLOW_PATH} from '../process-flow/HrProcessStepModel'

const HrProcessPage: FC = () => {
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [processes, setProcesses] = useState<IHrProcessModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState<IHrProcessModel | null>(null)
  const [deletingItem, setDeletingItem] = useState<IHrProcessModel | null>(null)

  const fetchProcesses = useCallback(async (page: number) => {
    setLoading(true)
    try {
      const payload = USE_HR_PROCESS_MOCK
        ? getMockHrProcessPage(page)
        : (await loadHrProcesses(page)).data.payload
      setProcesses(payload.content)
      setTotalPages(payload.totalPages)
    } catch (error) {
      showErrorToast('Süreçler yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchProcesses(0)
  }, [fetchProcesses])

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchProcesses(newPage)
      }
    },
    [totalPages, fetchProcesses]
  )

  const handleCreateClick = () => {
    setEditingItem(null)
    setShowForm(true)
  }

  const handleEdit = useCallback((process: IHrProcessModel) => {
    setEditingItem(process)
    setShowForm(true)
  }, [])

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingItem(null)
  }

  const handleSubmit = async (name: string) => {
    setSaving(true)
    try {
      if (editingItem?.id) {
        await updateHrProcess(editingItem.id, name)
        showSuccessToast('Süreç başarıyla güncellendi')
        await fetchProcesses(currentPage)
      } else {
        await createHrProcess(name)
        showSuccessToast('Süreç başarıyla oluşturuldu')
        setCurrentPage(0)
        await fetchProcesses(0)
      }
      handleCloseForm()
    } catch (error) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deletingItem?.id) return
    setLoading(true)
    try {
      await deleteHrProcess(deletingItem.id)
      showSuccessToast('Süreç başarıyla silindi')
      await fetchProcesses(currentPage)
    } catch (error) {
      showErrorToast('Süreç silinirken hata oluştu')
    } finally {
      setDeletingItem(null)
      setLoading(false)
    }
  }

  const handleFlow = useCallback(
    (process: IHrProcessModel) => {
      if (!process.id) return
      history.push({
        pathname: HR_PROCESS_FLOW_PATH,
        search: `?processId=${encodeURIComponent(process.id)}`,
        state: {processName: process.name},
      })
    },
    [history]
  )

  return (
    <>
      <PageTitle breadcrumbs={[]}>İK Süreç Tanımı</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>İK Süreçleri</span>
            <span className='text-muted mt-1 fw-bold fs-7'>İşe alım süreçleri ve akışları</span>
          </h3>
          <div className='card-toolbar'>
            <button type='button' className='btn btn-sm btn-primary' onClick={handleCreateClick}>
              <i className='bi bi-plus-lg fs-5'></i>
              Yeni Kayıt
            </button>
          </div>
        </div>
        <div className='card-body pt-0'>
          <HrProcessTable
            data={processes}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onFlow={handleFlow}
            onEdit={handleEdit}
            onDelete={setDeletingItem}
            loading={loading}
          />
        </div>
      </div>

      <HrProcessFormModal
        show={showForm}
        editingItem={editingItem}
        loading={saving}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
      />

      <ConfirmModal
        show={!!deletingItem}
        header='Silme Onayı'
        text={`"${deletingItem?.name ?? ''}" sürecini silmek istediğinize emin misiniz?`}
        handleClose={() => setDeletingItem(null)}
        applyOnClick={handleDelete}
      />

      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </>
  )
}

export {HrProcessPage}
